/**
 * ============================================================================
 * DESKTOP COMPONENT
 * ============================================================================
 *
 * The main orchestration component for the desktop environment.
 * Takes a configuration object and renders a complete desktop with
 * icons, windows, and background.
 *
 * @packageDocumentation
 */

"use client";

import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { WINDOW_CONFIG_WARNING_WHITELIST } from "../apps/registry";
import { getWallpaper } from "../config/wallpapers";
import { DesktopProvider } from "../contexts/DesktopContext";
import { NotificationProvider } from "../contexts/NotificationContext";
import {
  ThemeMode,
  ThemeProvider,
  useThemeOptional,
} from "../contexts/ThemeContext";
import { useAppBadgeCounts } from "../hooks/useAppBadgeCounts";
import { useDesktopPreferences } from "../hooks/useDesktopPreferences";
import { useMobileIconDrag } from "../hooks/useMobileIconDrag";
import { useWindowManager } from "../hooks/useWindowManager";
import { getStorageScope, scopedKey } from "../lib/scopedStorage";
import type {
  DesktopConfig,
  DesktopContextValue,
  DesktopIconConfig,
} from "../types";
import {
  isWindowMinimized,
  isWindowOpen,
  validateDesktopConfig,
} from "../types";
import { AppSwitcher, type AppSwitcherSlot } from "./AppSwitcher";
import { DesktopIcon, DesktopIconRef } from "./DesktopIcon";
import { GroupIcon } from "./GroupIcon";
import { GroupOverlay } from "./GroupOverlay";
import { IconContextMenu } from "./IconContextMenu";
import { MenuBar } from "./MenuBar";
import { MobileAppLibrary } from "./MobileAppLibrary";
import { MobileContextMenu } from "./MobileContextMenu";
import { NotificationToasts } from "./NotificationCenter";
import { Spotlight } from "./Spotlight";
import { Takeoff } from "./Takeoff";
import { WidgetLayer } from "./WidgetLayer";
import { Window } from "./Window";
import { WindowDragCloseTarget } from "./WindowDragCloseTarget";

/* ==========================================================================
   TYPES
   ========================================================================== */

export interface DesktopProps {
  /** Desktop configuration */
  config: DesktopConfig;
  /** Children to render (for custom content on desktop) */
  children?: React.ReactNode;
  /** Custom class name for the desktop container */
  className?: string;
  /** Initial theme mode (defaults to 'dark') */
  defaultTheme?: ThemeMode;
  /** Whether the boot sequence has completed (gates icon reveal + auto-open). Defaults to true. */
  booted?: boolean;
}

/* ==========================================================================
   COMPONENT
   ========================================================================== */

/**
 * The main Desktop component - renders a complete desktop environment.
 *
 * @remarks
 * This component:
 * - Validates the configuration on mount
 * - Manages all window state via useWindowManager
 * - Renders desktop icons from config
 * - Renders windows for each window config
 * - Handles background rendering
 * - Provides context to all children
 *
 * @example
 * ```tsx
 * const config: DesktopConfig = {
 *   windows: [
 *     { id: 'terminal', type: 'app', title: 'Terminal', component: Terminal },
 *     { id: 'browser', type: 'browser', title: 'Browser', url: '/web' },
 *   ],
 *   icons: [
 *     { id: 'term-icon', windowId: 'terminal', icon: <Terminal />, label: 'Terminal' },
 *     { id: 'web-icon', windowId: 'browser', icon: <Globe />, label: 'Web' },
 *   ],
 *   background: {
 *     type: 'gradient',
 *     gradient: 'linear-gradient(to bottom, #1a1a2e, #16213e)',
 *   },
 *   autoOpenWindow: 'terminal',
 * };
 *
 * function App() {
 *   return <Desktop config={config} />;
 * }
 * ```
 */
export function Desktop({
  config,
  children,
  className = "",
  defaultTheme = "dark",
  booted = true,
}: DesktopProps) {
  const inner = (
    <DesktopInner config={config} className={className} booted={booted}>
      {children}
    </DesktopInner>
  );

  return <ThemeProvider defaultTheme={defaultTheme}>{inner}</ThemeProvider>;
}

/**
 * Inner component that has access to theme context.
 */
function DesktopInner({
  config,
  children,
  className = "",
  booted = true,
}: Omit<DesktopProps, "defaultTheme">) {
  /* ========================================================================
     THEME
     ======================================================================== */

  const theme = useThemeOptional();
  const isDark = theme?.isDark ?? true;

  /* ========================================================================
     VALIDATION
     ======================================================================== */

  useEffect(() => {
    if (import.meta.env.PROD) return;
    const result = validateDesktopConfig(config);
    if (!result.valid) {
      // should we throw here? - probably - only in dev anyways
      console.error("Desktop: Invalid configuration", result.errors);
    }
    if (result.warnings.length > 0) {
      const filteredWarnings = result.warnings.filter(
        (w) =>
          !WINDOW_CONFIG_WARNING_WHITELIST.some((warningID) => {
            return w.includes(warningID);
          })
      );
      if (filteredWarnings.length > 0) {
        console.warn("Desktop: Configuration warnings", filteredWarnings);
      }
    }
  }, [config]);

  /* ========================================================================
     STATE
     ======================================================================== */

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768;
  });
  // Lazy-load wallpaper variants: only fetch the active theme on cold
  // boot, and only fetch the inactive variant once the user actually
  // toggles into it. First toggle pays a brief flash-of-gradient; every
  // subsequent toggle is a free opacity transition (browser-cached).
  // Most sessions never toggle, so this saves ~1 background image's
  // worth of bytes outright. Mutating the ref during render is safe —
  // Set.add is idempotent under StrictMode double-invocation.
  const seenThemesRef = useRef<Set<"dark" | "light">>(new Set());

  const [showIcons, setShowIcons] = useState(false);
  const [showSpotlight, setShowSpotlight] = useState(false);
  const [showTakeoff, setShowTakeoff] = useState(false);
  const [showAppSwitcher, setShowAppSwitcher] = useState(false);
  const [appSwitcherSlots, setAppSwitcherSlots] = useState<
    Map<string, AppSwitcherSlot>
  >(() => new Map());

  // Filter out icons (and their windows) that have a `visible` callback returning false
  const visibleConfig = useMemo((): DesktopConfig => {
    const visibleIcons = config.icons.filter((i) => !i.visible || i.visible());
    const visibleWindowIds = new Set(visibleIcons.map((i) => i.windowId));
    // Keep windows that either have a visible icon or aren't linked to any icon
    const allIconWindowIds = new Set(config.icons.map((i) => i.windowId));
    const visibleWindows = config.windows.filter(
      (w) => visibleWindowIds.has(w.id) || !allIconWindowIds.has(w.id)
    );
    return { ...config, icons: visibleIcons, windows: visibleWindows };
  }, [config]);

  // Track icon refs for minimize animations
  const iconRefs = useRef<Map<string, RefObject<DesktopIconRef | null>>>(
    new Map()
  );

  // Ref for the dock container (used to calculate drag-to-dock insertion index)
  const dockContainerRef = useRef<HTMLDivElement>(null);

  // Cascade slot tracker — maps each currently-open windowId to the slot
  // index it owns. Allocating the lowest unused slot on every acquire
  // means a window opened while only N others are visible always lands
  // at a distinct cascaded position, and slots freed on close get reused
  // (no drift toward the corner just because earlier windows came and
  // went).
  const cascadeSlots = useRef(new Map<string, number>());

  // Window manager hook
  const windowManager = useWindowManager(visibleConfig.windows, { isMobile });

  // Determine if any window is currently maximized (drives dock auto-hide)
  const hasMaximizedWindow = useMemo(() => {
    for (const instance of windowManager.windows.values()) {
      if (isWindowOpen(instance.state) && instance.state.isMaximized) {
        return true;
      }
    }
    return false;
  }, [windowManager.windows]);

  // Count of minimized (backgrounded) windows — drives the app switcher badges.
  const minimizedWindowCount = useMemo(() => {
    let n = 0;
    for (const instance of windowManager.windows.values()) {
      if (isWindowMinimized(instance.state)) n++;
    }
    return n;
  }, [windowManager.windows]);

  // Desktop preferences hook
  const desktopPreferences = useDesktopPreferences(visibleConfig);

  // Cache the resolved wallpaper URLs scoped by identity, plus an unscoped
  // "last active scope" pointer so main.tsx can locate the right cached
  // entry to preload before AuthContext resolves. This keeps wallpaper
  // choice (per-user data) properly isolated across guests / multiple
  // signed-in users on the same device.
  useEffect(() => {
    if (desktopPreferences.isLoading) return;
    const wp = desktopPreferences.backgroundId
      ? getWallpaper(
          desktopPreferences.backgroundId,
          desktopPreferences.customPexelsIds
        )
      : null;
    const scopedWallpaperKey = scopedKey("desktop-ui:active-wallpaper");
    try {
      if (wp) {
        localStorage.setItem(
          scopedWallpaperKey,
          JSON.stringify({ darkUrl: wp.darkUrl, lightUrl: wp.lightUrl })
        );
      } else {
        localStorage.removeItem(scopedWallpaperKey);
      }
      // Unscoped pointer to the last-active scope. main.tsx reads this
      // pre-auth to know which scoped wallpaper key to load for preload.
      localStorage.setItem("desktop-ui:last-active-scope", getStorageScope());
    } catch {
      // storage unavailable
    }
  }, [
    desktopPreferences.isLoading,
    desktopPreferences.backgroundId,
    desktopPreferences.customPexelsIds,
  ]);

  // Badge counts for group icon aggregation (desktop mode)
  const desktopBadgeCounts = useAppBadgeCounts();

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    iconId: string;
    x: number;
    y: number;
  } | null>(null);

  // Dock icon drag ghost state (rendered outside the dock container to avoid stacking context)
  const [dockDragGhost, setDockDragGhost] = useState<{
    iconId: string;
    cursorX: number;
    cursorY: number;
  } | null>(null);

  // Whether the virtual keyboard is currently open (detected via visualViewport shrink)
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  // Touch-capable device (iPad, phones, etc.) — used to enable touch-specific behaviors
  const isTouchDevice =
    typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;

  // Track which desktop icon is currently being dragged (to raise its z-index above the dock)
  const [draggingDesktopIconId, setDraggingDesktopIconId] = useState<
    string | null
  >(null);

  // Widget layer
  const [showWidgets, setShowWidgets] = useState(false);

  const desktopContainerRef = useRef<HTMLDivElement>(null);

  // Dock auto-hide: track whether dock is revealed via hover
  const [dockRevealed, setDockRevealed] = useState(false);
  const dockRevealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dockHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Menu bar auto-hide: track whether menu bar is revealed via hover
  const [menuBarRevealed, setMenuBarRevealed] = useState(false);
  const menuBarRevealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const menuBarHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  /* ========================================================================
     DEVICE DETECTION
     ======================================================================== */

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Stop missed OS-file drops from navigating the tab away. Apps that want
  // file drops handle them on their own elements with stopPropagation; anything
  // that bubbles up to the window is by definition unhandled and must be
  // swallowed before the browser opens the file.
  useEffect(() => {
    const isFileDrag = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types ?? []).includes("Files");
    const onDragOver = (e: DragEvent) => {
      if (isFileDrag(e)) e.preventDefault();
    };
    const onDrop = (e: DragEvent) => {
      if (isFileDrag(e)) e.preventDefault();
    };
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  // Native keyboard overlay: keep the desktop container full-screen when
  // the virtual keyboard opens. Instead of shrinking the container (which
  // creates a visible gap), we expose a --keyboard-height CSS custom
  // property so individual apps can add bottom padding if needed.
  useEffect(() => {
    const vv = window.visualViewport;
    const isTouchDev =
      typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;
    if (!vv || !isTouchDev) return;

    let rafId = 0;
    const KEYBOARD_THRESHOLD = 150;

    const onResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const heightDelta = window.innerHeight - vv.height;
        const kbHeight = heightDelta > KEYBOARD_THRESHOLD ? heightDelta : 0;
        const kbOpen = kbHeight > 0;
        setKeyboardOpen(kbOpen);

        // Expose keyboard height as CSS custom property for apps to use
        document.documentElement.style.setProperty(
          "--keyboard-height",
          `${kbHeight}px`
        );

        // Kill document scroll to prevent white gaps
        window.scrollTo(0, 0);
      });
    };

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    };

    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onScroll);
    return () => {
      cancelAnimationFrame(rafId);
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onScroll);
      document.documentElement.style.setProperty("--keyboard-height", "0px");
    };
  }, []);

  /* ========================================================================
     SPOTLIGHT KEYBOARD SHORTCUT
     ======================================================================== */

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+Shift+S (macOS) or Ctrl+Shift+S (Windows/Linux)
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === "s"
      ) {
        e.preventDefault();
        setShowSpotlight(true);
      }

      // Cmd+Shift+A (macOS) or Ctrl+Shift+A (Windows/Linux) — App Switcher
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === "a"
      ) {
        e.preventDefault();
        setShowAppSwitcher((prev) => !prev);
      }

      // F4 — Toggle Widgets layer (desktop only)
      if (e.key === "F4" && !isMobile) {
        e.preventDefault();
        setShowWidgets((prev) => !prev);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMobile]);

  /* ========================================================================
     ICON REGISTRATION
     ======================================================================== */

  const getIconRef = useCallback((iconId: string) => {
    return iconRefs.current.get(iconId) ?? null;
  }, []);

  const registerIconRef = useCallback(
    (iconId: string, ref: RefObject<DesktopIconRef | null>) => {
      iconRefs.current.set(iconId, ref);
    },
    []
  );

  /* ========================================================================
     CASCADE POSITIONING
     ======================================================================== */

  const acquireCascadeSlot = useCallback((windowId: string): number => {
    const slots = cascadeSlots.current;
    // Idempotent: re-opening or rerunning an effect for the same id keeps
    // the same slot rather than leaking new ones.
    const existing = slots.get(windowId);
    if (existing !== undefined) return existing;

    // Pick the lowest non-negative integer not currently held by any
    // other open window. With slot release on close, the set of in-use
    // slots stays compact and the next open lands on a distinct
    // (non-overlapping) cascaded position.
    const used = new Set(slots.values());
    let slot = 0;
    while (used.has(slot)) slot++;
    slots.set(windowId, slot);
    return slot;
  }, []);

  const releaseCascadeSlot = useCallback((windowId: string) => {
    cascadeSlots.current.delete(windowId);
  }, []);

  /* ========================================================================
     ICON CLICK HANDLERS
     ======================================================================== */

  const handleIconClick = useCallback(
    (iconConfig: DesktopIconConfig) => {
      const { windowId, onIconClick } = iconConfig;
      const instance = windowManager.windows.get(windowId);
      if (!instance) return;

      const state = instance.state;

      if (isWindowOpen(state)) {
        // Already open - just focus it
        windowManager.focusWindow(windowId);
      } else if (isWindowMinimized(state)) {
        // Minimized - restore it
        windowManager.restoreWindow(windowId);
      } else {
        // Closed - open it
        windowManager.openWindow(windowId);
      }

      // Call the optional onIconClick callback (e.g., for shortcut icons)
      if (onIconClick) {
        // Small delay to ensure the window is ready
        setTimeout(onIconClick, 50);
      }
    },
    [windowManager]
  );

  /* ========================================================================
     AUTO-OPEN WINDOW
     ======================================================================== */

  const hasAutoOpened = useRef(false);
  const deepLinkedWindowRef = useRef<string | null>(null);

  useEffect(() => {
    if (!booted || desktopPreferences.isLoading) return;

    // Disable auto-open on mobile - show empty desktop by default
    const isMobileDevice = window.innerWidth < 768;

    // Deep link: auto-open window from /app/:id path (or legacy ?app= query parameter)
    // Path-based links survive WhatsApp/iMessage/social app redirects that strip query params
    const appPathMatch = window.location.pathname.match(/^\/app\/([^/]+)/);
    const params = new URLSearchParams(window.location.search);
    const deepLinkApp = appPathMatch
      ? decodeURIComponent(appPathMatch[1])
      : params.get("app"); // backwards compat for old ?app= links

    if (deepLinkApp) {
      const windowExists = config.windows.some((w) => w.id === deepLinkApp);
      if (windowExists && !hasAutoOpened.current) {
        hasAutoOpened.current = true;
        deepLinkedWindowRef.current = deepLinkApp;
        windowManager.openWindow(deepLinkApp);
      }
    }

    // Check if any windows are already open (from persisted state)
    const hasOpenWindows = Array.from(windowManager.windows.values()).some(
      (instance) =>
        instance.state.status === "open" ||
        instance.state.status === "minimized"
    );

    // Skip auto-open if we have persisted open windows
    if (hasOpenWindows) {
      hasAutoOpened.current = true;
      return;
    }

    if (config.autoOpenWindow && !hasAutoOpened.current && !isMobileDevice) {
      hasAutoOpened.current = true;
      // Delay to allow icons to render and get their positions
      const timer = setTimeout(() => {
        windowManager.openWindow(config.autoOpenWindow!);
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booted, desktopPreferences.isLoading]); // Run once booted and preferences loaded

  // Clean deep link URL when the deep-linked window is closed
  useEffect(() => {
    const id = deepLinkedWindowRef.current;
    if (!id) return;
    const instance = windowManager.windows.get(id);
    if (instance && instance.state.status === "closed") {
      deepLinkedWindowRef.current = null;
      window.history.replaceState({}, "", "/");
    }
  }, [windowManager.windows]);

  /* ========================================================================
     SHOW ICONS AFTER MOUNT
     ======================================================================== */

  useEffect(() => {
    if (!booted || desktopPreferences.isLoading) return;
    const timer = setTimeout(() => setShowIcons(true), 100);
    return () => clearTimeout(timer);
  }, [booted, desktopPreferences.isLoading]);

  // Listen for desktop:open-window events (used by cross-app internal navigation)
  useEffect(() => {
    const handler = (e: Event) => {
      const { windowId } = (e as CustomEvent<{ windowId: string }>).detail;
      if (windowId) windowManager.openWindow(windowId);
    };
    window.addEventListener("desktop:open-window", handler);
    return () => window.removeEventListener("desktop:open-window", handler);
  }, [windowManager]);

  /* ========================================================================
     CLOSE TAKEOFF WHEN A NEW WINDOW OPENS
     ======================================================================== */

  // Snapshot open-window count so we can detect *increases* (not existing opens)
  const prevOpenCountRef = useRef<number | null>(null);

  useEffect(() => {
    let count = 0;
    for (const instance of windowManager.windows.values()) {
      if (instance.state.status === "open") count++;
    }

    const prev = prevOpenCountRef.current;
    prevOpenCountRef.current = count;

    // Close Takeoff only when the count goes up (a window was just opened)
    if (showTakeoff && prev !== null && count > prev) {
      setShowTakeoff(false);
    }
  }, [showTakeoff, windowManager.windows]);

  /* ========================================================================
     BACKGROUND CLICK
     ======================================================================== */

  /**
   * Calculate dock insertion index from a cursor X position
   */
  // Ref to hold dockHeight for use in callbacks declared before the value is computed
  const dockHeightRef = useRef(0);

  const handleDesktopIconDraggingChange = useCallback(
    (iconId: string, isDragging: boolean) => {
      setDraggingDesktopIconId(isDragging ? iconId : null);
      // When drag ends, clamp position so the icon can't rest behind the dock
      const dh = dockHeightRef.current;
      if (!isDragging && dh > 0) {
        const maxYPercent =
          ((window.innerHeight - dh - 20) / window.innerHeight) * 100;
        const di = desktopPreferences.desktopIcons.find((d) => d.id === iconId);
        if (di && di.y > maxYPercent) {
          desktopPreferences.updateDesktopIconPosition(
            iconId,
            di.x,
            maxYPercent
          );
        }
      }
    },
    [desktopPreferences]
  );

  const handleDockDragMove = useCallback(
    (iconId: string, cursorX: number, cursorY: number) => {
      setDockDragGhost({ iconId, cursorX, cursorY });
    },
    []
  );

  const handleDockDragEnd = useCallback((_iconId: string) => {
    setDockDragGhost(null);
  }, []);

  const handleBackgroundClick = useCallback(() => {
    windowManager.deactivateAll();
    setContextMenu(null);
  }, [windowManager]);

  // Clear persisted geometry when a window is closed via drag-to-trash
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail.windowId) {
        windowManager.clearGeometry(detail.windowId);
      }
    };
    window.addEventListener("window:drag-close", handler);
    return () => window.removeEventListener("window:drag-close", handler);
  }, [windowManager]);

  /* ========================================================================
     CONTEXT VALUE
     ======================================================================== */

  const contextValue: DesktopContextValue = useMemo(
    () => ({
      ...windowManager,
      getIconRef: getIconRef as (
        iconId: string
      ) => RefObject<HTMLDivElement> | null,
      registerIconRef: registerIconRef as unknown as (
        iconId: string,
        ref: RefObject<HTMLDivElement>
      ) => void,
      isMobile,
      acquireCascadeSlot,
      releaseCascadeSlot,
      layoutMode: desktopPreferences.layoutMode,
      setLayoutMode: desktopPreferences.setLayoutMode,
      hideMenuBarInFullScreen: desktopPreferences.hideMenuBarInFullScreen,
      setHideMenuBarInFullScreen: desktopPreferences.setHideMenuBarInFullScreen,
      backgroundId: desktopPreferences.backgroundId,
      setBackgroundId: desktopPreferences.setBackgroundId,
      customPexelsIds: desktopPreferences.customPexelsIds,
      addCustomPexelsId: desktopPreferences.addCustomPexelsId,
      removeCustomPexelsId: desktopPreferences.removeCustomPexelsId,
      shikiTheme: desktopPreferences.shikiTheme,
      setShikiTheme: desktopPreferences.setShikiTheme,
      codeEditorFormatOnSave: desktopPreferences.codeEditorFormatOnSave,
      setCodeEditorFormatOnSave: desktopPreferences.setCodeEditorFormatOnSave,
      desktopPreferencesLoading: desktopPreferences.isLoading,
      resetIconPreferences: desktopPreferences.resetIconPositions,
      isMobileLayoutAtDefaults: desktopPreferences.isMobileLayoutAtDefaults,
      isDesktopLayoutAtDefaults: desktopPreferences.isDesktopLayoutAtDefaults,
    }),
    [
      windowManager,
      getIconRef,
      registerIconRef,
      isMobile,
      acquireCascadeSlot,
      releaseCascadeSlot,
      desktopPreferences.layoutMode,
      desktopPreferences.setLayoutMode,
      desktopPreferences.backgroundId,
      desktopPreferences.customPexelsIds,
      desktopPreferences.shikiTheme,
      desktopPreferences.setShikiTheme,
      desktopPreferences.codeEditorFormatOnSave,
      desktopPreferences.setCodeEditorFormatOnSave,
      desktopPreferences.isLoading,
      desktopPreferences.resetIconPositions,
      desktopPreferences.isMobileLayoutAtDefaults,
      desktopPreferences.isDesktopLayoutAtDefaults,
    ]
  );

  /* ========================================================================
     ICON LAYOUT
     ======================================================================== */

  const MENU_BAR_HEIGHT = 28;

  const iconLayout = config.iconLayout ?? {
    startPosition: "top-left",
    direction: "vertical",
    gap: 16,
    padding: 16,
  };

  const isDockLayout = !isMobile && desktopPreferences.layoutMode === "dock";
  const effectiveLayoutMode = isMobile ? "dock" : desktopPreferences.layoutMode;

  // Whether the dock is currently auto-hidden:
  // - maximized window + not hovered (desktop behavior), OR
  // - keyboard open on a touch device (iPad/phone — dock covers app content otherwise)
  const dockHiddenByKeyboard = keyboardOpen && isTouchDevice;
  const dockAutoHidden =
    (hasMaximizedWindow && !dockRevealed && isDockLayout) ||
    dockHiddenByKeyboard;

  // Whether the menu bar should auto-hide (setting enabled + maximized window + not mobile)
  const shouldAutoHideMenuBar =
    hasMaximizedWindow &&
    !isMobile &&
    desktopPreferences.hideMenuBarInFullScreen;
  // Whether the menu bar is currently hidden (auto-hide active and not revealed by hover)
  const menuBarAutoHidden = shouldAutoHideMenuBar && !menuBarRevealed;

  // Reset dock/menuBar revealed when no windows are maximized
  useEffect(() => {
    if (!hasMaximizedWindow) {
      setDockRevealed(false);
      setMenuBarRevealed(false);
      if (dockRevealTimerRef.current) {
        clearTimeout(dockRevealTimerRef.current);
        dockRevealTimerRef.current = null;
      }
      if (dockHideTimerRef.current) {
        clearTimeout(dockHideTimerRef.current);
        dockHideTimerRef.current = null;
      }
      if (menuBarRevealTimerRef.current) {
        clearTimeout(menuBarRevealTimerRef.current);
        menuBarRevealTimerRef.current = null;
      }
      if (menuBarHideTimerRef.current) {
        clearTimeout(menuBarHideTimerRef.current);
        menuBarHideTimerRef.current = null;
      }
    }
  }, [hasMaximizedWindow]);

  // Handlers for the dock trigger zone and dock hover
  const handleDockTriggerEnter = useCallback(() => {
    if (!hasMaximizedWindow || !isDockLayout) return;
    // Clear any pending hide timer
    if (dockHideTimerRef.current) {
      clearTimeout(dockHideTimerRef.current);
      dockHideTimerRef.current = null;
    }
    // Start reveal timer (300ms delay to avoid accidental triggers)
    dockRevealTimerRef.current = setTimeout(() => {
      setDockRevealed(true);
      dockRevealTimerRef.current = null;
    }, 300);
  }, [hasMaximizedWindow, isDockLayout]);

  const handleDockTriggerLeave = useCallback(() => {
    // Cancel pending reveal
    if (dockRevealTimerRef.current) {
      clearTimeout(dockRevealTimerRef.current);
      dockRevealTimerRef.current = null;
    }
  }, []);

  const handleDockAreaLeave = useCallback(() => {
    if (!hasMaximizedWindow) return;
    // Cancel pending reveal
    if (dockRevealTimerRef.current) {
      clearTimeout(dockRevealTimerRef.current);
      dockRevealTimerRef.current = null;
    }
    // Delay hide slightly so clicking dock icons isn't jarring
    dockHideTimerRef.current = setTimeout(() => {
      setDockRevealed(false);
      dockHideTimerRef.current = null;
    }, 200);
  }, [hasMaximizedWindow]);

  const handleDockAreaEnter = useCallback(() => {
    // If dock is already revealed and user moves back onto it, cancel hide
    if (dockHideTimerRef.current) {
      clearTimeout(dockHideTimerRef.current);
      dockHideTimerRef.current = null;
    }
  }, []);

  // Menu bar hover handlers (same pattern as dock)
  const handleMenuBarTriggerEnter = useCallback(() => {
    if (
      !hasMaximizedWindow ||
      isMobile ||
      !desktopPreferences.hideMenuBarInFullScreen
    )
      return;
    if (menuBarHideTimerRef.current) {
      clearTimeout(menuBarHideTimerRef.current);
      menuBarHideTimerRef.current = null;
    }
    menuBarRevealTimerRef.current = setTimeout(() => {
      setMenuBarRevealed(true);
      menuBarRevealTimerRef.current = null;
    }, 300);
  }, [
    hasMaximizedWindow,
    isMobile,
    desktopPreferences.hideMenuBarInFullScreen,
  ]);

  const handleMenuBarTriggerLeave = useCallback(() => {
    if (menuBarRevealTimerRef.current) {
      clearTimeout(menuBarRevealTimerRef.current);
      menuBarRevealTimerRef.current = null;
    }
  }, []);

  const handleMenuBarAreaLeave = useCallback(() => {
    if (!hasMaximizedWindow) return;
    if (menuBarRevealTimerRef.current) {
      clearTimeout(menuBarRevealTimerRef.current);
      menuBarRevealTimerRef.current = null;
    }
    menuBarHideTimerRef.current = setTimeout(() => {
      setMenuBarRevealed(false);
      menuBarHideTimerRef.current = null;
    }, 300);
  }, [hasMaximizedWindow]);

  const handleMenuBarAreaEnter = useCallback(() => {
    if (menuBarHideTimerRef.current) {
      clearTimeout(menuBarHideTimerRef.current);
      menuBarHideTimerRef.current = null;
    }
  }, []);

  // Filter out hidden icons and desktop-placed icons, then apply max dock limit
  const hiddenSet = new Set(desktopPreferences.hiddenIcons);
  const desktopIconSet = new Set(
    desktopPreferences.desktopIcons.map((d) => d.id)
  );
  const dockNonHiddenIcons = desktopPreferences.dockIcons.filter(
    (id) => !hiddenSet.has(id) && !desktopIconSet.has(id)
  );

  // Dynamically calculate how many dock icons fit in the viewport.
  // Excess icons are accessible via Takeoff.
  const [maxDockIcons, setMaxDockIcons] = useState(20);

  useEffect(() => {
    const ICON_SIZE = 56;
    const calculateMaxIcons = () => {
      const vw = window.innerWidth;
      const gap = iconLayout.gap;
      const pad = iconLayout.padding + 8; // horizontal padding per side
      const dockMargin = 32; // breathing room on each side of dock
      // Fixed elements: Takeoff button + divider + pinned-end divider + pinned-end icon(s)
      const pinnedEndCount = visibleConfig.icons.filter(
        (i) => i.pinned === "end"
      ).length;
      const dividerWidth = 1 + 4; // 1px divider + 2px margin each side
      const takeoffWidth = ICON_SIZE; // Takeoff button
      const pinnedEndWidth =
        pinnedEndCount > 0
          ? dividerWidth +
            gap +
            pinnedEndCount * ICON_SIZE +
            (pinnedEndCount - 1) * gap
          : 0;
      const fixedWidth =
        pad * 2 +
        takeoffWidth +
        gap +
        dividerWidth +
        gap +
        pinnedEndWidth +
        dockMargin * 2;
      const availableForIcons = vw - fixedWidth;
      const maxFit = Math.max(
        1,
        Math.floor((availableForIcons + gap) / (ICON_SIZE + gap))
      );
      setMaxDockIcons(Math.min(maxFit, 20));
    };
    calculateMaxIcons();
    window.addEventListener("resize", calculateMaxIcons);
    return () => window.removeEventListener("resize", calculateMaxIcons);
  }, [iconLayout.gap, iconLayout.padding, visibleConfig.icons]);

  // The user's "visible dock" is now a PERSISTED preference. If it hasn't
  // been seeded yet (first render after a deploy, or a migrated account),
  // compute an initial seed from dockNonHiddenIcons.slice(0, maxDockIcons)
  // and persist it so future refreshes render EXACTLY those icons instead
  // of auto-filling to viewport width.
  const persistedVisible = desktopPreferences.dockVisibleIcons;
  const negativePriorityIds = useMemo(
    () =>
      new Set(
        visibleConfig.icons
          .filter((i) => (i.dockPriority ?? 0) < 0)
          .map((i) => i.id)
      ),
    [visibleConfig.icons]
  );
  const visibleSet = useMemo<Set<string>>(() => {
    if (persistedVisible !== undefined) {
      return new Set(persistedVisible);
    }
    const eligible = dockNonHiddenIcons.filter(
      (id) => !negativePriorityIds.has(id)
    );
    return new Set(eligible.slice(0, maxDockIcons));
  }, [persistedVisible, dockNonHiddenIcons, negativePriorityIds, maxDockIcons]);

  // Preserve dock order from dockNonHiddenIcons, filtered to the visible set,
  // then clamp to what fits in the viewport. The viewport clamp is purely
  // cosmetic — the visible set is still the authoritative user choice and
  // will render fully again when the viewport grows back.
  const dockVisibleIcons = dockNonHiddenIcons
    .filter((id) => visibleSet.has(id))
    .slice(0, maxDockIcons);

  // One-time seed: if the user doesn't have a persisted visible set yet (and
  // prefs have finished loading), write the computed seed so subsequent
  // refreshes are deterministic.
  const setDockVisibleIcons = desktopPreferences.setDockVisibleIcons;
  useEffect(() => {
    if (desktopPreferences.isLoading) return;
    if (persistedVisible !== undefined) return;
    if (dockNonHiddenIcons.length === 0) return;
    const eligible = dockNonHiddenIcons.filter(
      (id) => !negativePriorityIds.has(id)
    );
    setDockVisibleIcons(eligible.slice(0, maxDockIcons));
  }, [
    desktopPreferences.isLoading,
    persistedVisible,
    dockNonHiddenIcons,
    negativePriorityIds,
    maxDockIcons,
    setDockVisibleIcons,
  ]);

  // Resolve cursorX to an insertion index by finding dock icon wrappers tagged with data-dock-idx
  const getDockInsertionIndex = useCallback(
    (cursorX: number, excludeIconId?: string): number => {
      const container = dockContainerRef.current;
      if (!container) return 0;

      const wrappers = Array.from(
        container.querySelectorAll<HTMLElement>("[data-dock-idx]")
      );
      const remaining: DOMRect[] = [];
      for (const el of wrappers) {
        if (excludeIconId && el.dataset.dockId === excludeIconId) continue;
        remaining.push(el.getBoundingClientRect());
      }

      for (let i = 0; i < remaining.length; i++) {
        const midX = remaining[i].left + remaining[i].width / 2;
        if (cursorX < midX) {
          return i;
        }
      }
      return remaining.length;
    },
    []
  );

  // Cursor-based dock reorder using icon IDs.
  // Maps the visual insertion index (among visible icons) to the correct
  // position in the full dockOrder (which may contain non-visible overflow icons).
  const handleDockReorder = useCallback(
    (fromIndex: number, cursorX: number) => {
      const draggedIconId = dockVisibleIcons[fromIndex];
      if (!draggedIconId) return;
      const toVisualIndex = getDockInsertionIndex(cursorX, draggedIconId);

      // Remaining visible icons after the dragged one is conceptually removed
      const remainingVisible = dockVisibleIcons.filter(
        (id) => id !== draggedIconId
      );
      // Full dockOrder with the dragged icon removed
      const filteredDockOrder = desktopPreferences.dockIcons.filter(
        (id) => id !== draggedIconId
      );

      let dockOrderIndex: number;
      if (toVisualIndex >= remainingVisible.length) {
        // Past the last visible icon — insert after the last visible icon's position in dockOrder
        const lastVisibleId = remainingVisible[remainingVisible.length - 1];
        dockOrderIndex = filteredDockOrder.indexOf(lastVisibleId) + 1;
      } else {
        // Insert before the anchor icon at the visual insertion point
        const anchorId = remainingVisible[toVisualIndex];
        dockOrderIndex = filteredDockOrder.indexOf(anchorId);
      }

      desktopPreferences.reorderDockById(draggedIconId, dockOrderIndex);
    },
    [getDockInsertionIndex, desktopPreferences, dockVisibleIcons]
  );

  // Estimate dock height so windows avoid overlapping it
  // Dock = bottom offset (padding) + container padding top/bottom (padding*2) + icon (56px) + border (2px) + gap
  const DOCK_ICON_SIZE = 56; // w-14 h-14 = 56px (desktop dock icons)
  const DOCK_GAP = 12; // extra breathing room above the dock
  const dockVerticalPad = Math.max(6, iconLayout.padding - 8);
  const rawDockHeight = isDockLayout
    ? iconLayout.padding + dockVerticalPad * 2 + DOCK_ICON_SIZE + 2 + DOCK_GAP
    : 0;
  // When dock is auto-hidden, treat height as 0 so windows can use full viewport
  const dockHeight = dockAutoHidden ? 0 : rawDockHeight;
  dockHeightRef.current = dockHeight;

  const getIconContainerStyle = (): React.CSSProperties => {
    // Dock layout - centered at bottom, responsive with horizontal scroll on mobile
    if (isDockLayout) {
      // Compute the dock transform: handle boot animation, auto-hide, and centering
      const shouldHideDock =
        (hasMaximizedWindow && !dockRevealed) || dockHiddenByKeyboard;
      let dockTransform: string;
      if (isMobile) {
        dockTransform =
          !showIcons || dockHiddenByKeyboard
            ? "translateY(100px)"
            : "translateY(0)";
      } else if (!showIcons) {
        dockTransform = "translateX(-50%) translateY(100px)";
      } else if (shouldHideDock) {
        // Slide dock below the viewport
        dockTransform = `translateX(-50%) translateY(${rawDockHeight + iconLayout.padding}px)`;
      } else {
        dockTransform = "translateX(-50%) translateY(0)";
      }

      return {
        position: "absolute",
        bottom: iconLayout.padding,
        left: isMobile ? iconLayout.padding : "50%",
        right: isMobile ? iconLayout.padding : "auto",
        transform: dockTransform,
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-end",
        gap: `${isMobile ? Math.max(8, iconLayout.gap - 4) : Math.max(6, iconLayout.gap - 6)}px`,
        padding: isMobile
          ? `${Math.max(8, iconLayout.padding - 4)}px ${Math.max(12, iconLayout.padding)}px`
          : `${Math.max(6, iconLayout.padding - 8)}px ${Math.max(10, iconLayout.padding)}px`,
        zIndex: 50,
        transition: "transform 0.3s ease-out, opacity 0.3s ease-out",
        opacity: shouldHideDock ? 0 : 1,
        pointerEvents: shouldHideDock ? "none" : "auto",
        backgroundColor: "rgba(255, 255, 255, 0.15)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderRadius: "16px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        overflowX: "visible",
        overflowY: "visible",
        maxWidth: "none",
      };
    }

    // Traditional icon layout
    const style: React.CSSProperties = {
      position: "absolute",
      display: "flex",
      gap: `${iconLayout.gap}px`,
      padding: `${iconLayout.padding}px`,
      zIndex: 10,
      transition: "transform 0.5s ease-out",
      transform: showIcons ? "translateX(0)" : "translateX(-300px)",
    };

    // Position based on startPosition (account for menu bar on top positions)
    switch (iconLayout.startPosition) {
      case "top-left":
        style.top = MENU_BAR_HEIGHT + 12;
        style.left = 0;
        break;
      case "top-right":
        style.top = MENU_BAR_HEIGHT + 12;
        style.right = 0;
        break;
      case "bottom-left":
        style.bottom = 40;
        style.left = 0;
        break;
      case "bottom-right":
        style.bottom = 40;
        style.right = 0;
        break;
    }

    // Direction
    style.flexDirection =
      iconLayout.direction === "vertical" ? "column" : "row";

    return style;
  };

  /* ========================================================================
     BACKGROUND RENDERING
     ======================================================================== */

  const getBackgroundStyle = (bg: typeof config.background) => {
    if (!bg) return {};

    const style: React.CSSProperties = {};

    switch (bg.type) {
      case "color":
        style.backgroundColor = bg.color;
        break;
      case "image":
        style.backgroundImage = `url(${bg.url})`;
        style.backgroundSize = bg.size ?? "cover";
        style.backgroundPosition = bg.position ?? "center";
        break;
      case "gradient":
        style.background = bg.gradient;
        break;
    }

    return style;
  };

  const renderBackground = () => {
    // If user has a wallpaper preference, use it; otherwise fall back to config
    const wallpaper = desktopPreferences.backgroundId
      ? getWallpaper(
          desktopPreferences.backgroundId,
          desktopPreferences.customPexelsIds
        )
      : null;

    const darkBg = wallpaper
      ? {
          type: "image" as const,
          url: wallpaper.darkUrl,
          size: "cover" as const,
          position: "center",
        }
      : (config.darkBackground ?? config.background);
    const lightBg = wallpaper
      ? {
          type: "image" as const,
          url: wallpaper.lightUrl,
          size: "cover" as const,
          position: "center",
        }
      : (config.lightBackground ?? config.background);

    // Default backgrounds if none provided
    const defaultDarkStyle = {
      background: "linear-gradient(to bottom, #18181b, #27272a)",
    };
    const defaultLightStyle = {
      background: "linear-gradient(to bottom, #f4f4f5, #e4e4e7)",
    };

    // Only the currently-active theme resolves to a real image URL.
    // The inactive variant uses a gradient placeholder until the user
    // toggles into it for the first time, at which point it gets
    // recorded in seenThemesRef and stays loaded for the rest of the
    // session.
    seenThemesRef.current.add(isDark ? "dark" : "light");
    const darkSeen = seenThemesRef.current.has("dark");
    const lightSeen = seenThemesRef.current.has("light");
    const darkStyle =
      darkBg && darkSeen ? getBackgroundStyle(darkBg) : defaultDarkStyle;
    const lightStyle =
      lightBg && lightSeen ? getBackgroundStyle(lightBg) : defaultLightStyle;

    return (
      <>
        {/* Light background - always behind */}
        <div className="absolute inset-0" style={lightStyle} />
        {lightBg?.overlay && (
          <div
            className="absolute inset-0"
            style={{ background: lightBg.overlay }}
          />
        )}

        {/* Dark background - fades on top */}
        <div
          className="absolute inset-0 transition-opacity duration-500 ease-in-out"
          style={{
            ...darkStyle,
            opacity: isDark ? 1 : 0,
          }}
        />
        {darkBg?.overlay && (
          <div
            className="absolute inset-0 transition-opacity duration-500 ease-in-out"
            style={{
              background: darkBg.overlay,
              opacity: isDark ? 1 : 0,
            }}
          />
        )}
      </>
    );
  };

  /* ========================================================================
     RENDER
     ======================================================================== */

  return (
    <NotificationProvider>
      <DesktopProvider value={contextValue}>
        <div
          ref={desktopContainerRef}
          className={`fixed inset-0 overflow-hidden ${className}`}
          style={{ backgroundColor: isDark ? "#18181b" : "#f4f4f5" }}
        >
          {/* Background — hidden while preferences load to avoid flashing defaults */}
          <div
            className="absolute inset-0 transition-opacity duration-300 ease-in-out"
            style={{
              opacity: desktopPreferences.isLoading ? 0 : 1,
              // On mobile, suppress iOS long-press callout/selection on the
              // wallpaper itself so entering jiggle mode doesn't fight the
              // native image-save/selection gesture. Desktop keeps defaults.
              ...(isMobile && {
                WebkitTouchCallout: "none",
                WebkitUserSelect: "none",
                userSelect: "none",
              }),
            }}
          >
            {renderBackground()}
          </div>

          {/* Clickable background to deactivate windows */}
          <div
            className="absolute inset-0 z-0"
            data-desktop-background="true"
            onClick={handleBackgroundClick}
          />

          {/* Menu Bar — auto-hide trigger zone (thin strip at top when hidden) */}
          {menuBarAutoHidden && (
            <div
              className="fixed left-0 right-0 top-0 z-[101] h-[3px]"
              onMouseEnter={handleMenuBarTriggerEnter}
              onMouseLeave={handleMenuBarTriggerLeave}
            />
          )}
          <MenuBar
            autoHidden={menuBarAutoHidden}
            onMouseEnter={menuBarRevealed ? handleMenuBarAreaEnter : undefined}
            onMouseLeave={menuBarRevealed ? handleMenuBarAreaLeave : undefined}
            onOpenSpotlight={() => setShowSpotlight(true)}
            onToggleWidgets={
              isMobile ? undefined : () => setShowWidgets((prev) => !prev)
            }
            onToggleAppSwitcher={() => setShowAppSwitcher((prev) => !prev)}
            appSwitcherOpen={showAppSwitcher}
          />

          {/* Desktop Icons - Mobile Grid or Desktop Dock/Icons */}
          {isMobile ? (
            <MobileIconGrid
              icons={visibleConfig.icons}
              mobileGridPositions={desktopPreferences.mobileGridPositions}
              mobileDockPositions={desktopPreferences.mobileDockPositions}
              windowManager={windowManager}
              onIconClick={handleIconClick}
              registerIconRef={registerIconRef}
              showIcons={showIcons}
              onOpenSpotlight={() => setShowSpotlight(true)}
              onOpenAppSwitcher={() => setShowAppSwitcher(true)}
              appSwitcherCount={minimizedWindowCount}
              onPlaceInGrid={desktopPreferences.placeIconInGrid}
              onPlaceInDock={desktopPreferences.placeIconInDock}
              onRemoveFromGrid={desktopPreferences.removeFromMobileGrid}
              onAddToHomeScreen={desktopPreferences.addToHomeScreen}
              isSpotlightOpen={showSpotlight}
              iconGroups={desktopPreferences.iconGroups}
              onCreateGroup={desktopPreferences.createGroup}
              onDissolveGroup={desktopPreferences.dissolveGroup}
              onAddToGroup={desktopPreferences.addToGroup}
              onRemoveFromGroup={desktopPreferences.removeFromGroup}
              onRenameGroup={desktopPreferences.renameGroup}
              onReorderGroup={desktopPreferences.reorderGroup}
            />
          ) : effectiveLayoutMode === "dock" ? (
            <>
              {/* Dock auto-hide trigger zone — thin strip at bottom of screen */}
              {hasMaximizedWindow && !dockRevealed && (
                <div
                  onMouseEnter={handleDockTriggerEnter}
                  onMouseLeave={handleDockTriggerLeave}
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    zIndex: 51,
                  }}
                />
              )}
              {/* Dock bar */}
              <div
                ref={dockContainerRef}
                data-hurricane-dock
                style={getIconContainerStyle()}
                onMouseEnter={handleDockAreaEnter}
                onMouseLeave={handleDockAreaLeave}
              >
                {/* Takeoff launcher icon — always first in dock */}
                <div className="flex flex-shrink-0 flex-col items-center">
                  <button
                    onClick={() => setShowTakeoff(true)}
                    title="Takeoff"
                    className="transition-all duration-200 ease-out hover:-translate-y-2 hover:scale-125"
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      border: "none",
                      cursor: "pointer",
                      background: "rgba(128, 128, 128, 0.5)",
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      gridTemplateRows: "repeat(3, 1fr)",
                      gap: 3,
                      padding: 10,
                    }}
                  >
                    {[
                      "#FF453A",
                      "#FF9F0A",
                      "#30D158",
                      "#0A84FF",
                      "#BF5AF2",
                      "#FFD60A",
                      "#FF375F",
                      "#5AC8FA",
                      "#AF52DE",
                    ].map((color, i) => (
                      <div
                        key={i}
                        style={{
                          borderRadius: 2,
                          backgroundColor: color,
                          aspectRatio: "1 / 1",
                          minHeight: 0,
                        }}
                      />
                    ))}
                  </button>
                  {/* Spacer matching dock icon indicator dot */}
                  <div className="mt-1 h-1 w-1 opacity-0" />
                </div>
                {/* Divider after Takeoff */}
                <div
                  style={{
                    width: "1px",
                    height: "32px",
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                    flexShrink: 0,
                    alignSelf: "center",
                    marginLeft: "2px",
                    marginRight: "2px",
                  }}
                />
                {dockVisibleIcons.map((iconId, idx) => {
                  // Check if this is a group
                  const group = desktopPreferences.iconGroups[iconId];
                  if (group) {
                    const groupIconConfigs = group.iconIds
                      .map((id) => visibleConfig.icons.find((i) => i.id === id))
                      .filter((c): c is DesktopIconConfig => !!c);
                    return (
                      <div
                        key={iconId}
                        data-dock-idx={idx}
                        data-dock-id={iconId}
                      >
                        <GroupIcon
                          iconConfigs={groupIconConfigs}
                          groupName={group.name}
                          groupId={iconId}
                          isDock={true}
                          badgeCount={group.iconIds.reduce(
                            (sum, id) =>
                              sum + (desktopBadgeCounts.get(id) ?? 0),
                            0
                          )}
                          onClick={() => {
                            // TODO: open group overlay on desktop
                          }}
                        />
                      </div>
                    );
                  }

                  const iconConfig = visibleConfig.icons.find(
                    (i) => i.id === iconId
                  );
                  if (!iconConfig) return null;

                  const windowInstance = windowManager.windows.get(
                    iconConfig.windowId
                  );
                  const windowIsOpen =
                    windowInstance?.state.status === "open" ||
                    windowInstance?.state.status === "minimized";
                  const isActive = iconConfig.isActiveIcon
                    ? iconConfig.isActiveIcon(windowIsOpen)
                    : windowIsOpen;

                  return (
                    <div
                      key={iconConfig.id}
                      data-dock-idx={idx}
                      data-dock-id={iconConfig.id}
                    >
                      <DesktopIconWrapper
                        config={iconConfig}
                        onClick={() => handleIconClick(iconConfig)}
                        onRegisterRef={registerIconRef}
                        isMobile={false}
                        isDock={true}
                        isOpen={isActive}
                        onDockReorder={handleDockReorder}
                        dockIndex={idx}
                        onContextMenu={(id, x, y) =>
                          setContextMenu({ iconId: id, x, y })
                        }
                        onDragOffDock={(id, x, y) =>
                          desktopPreferences.moveIconToDesktop(id, x, y)
                        }
                        dockHeight={dockHeight}
                        onDockDragMove={handleDockDragMove}
                        onDockDragEnd={handleDockDragEnd}
                      />
                    </div>
                  );
                })}

                {/* Pinned end icons (e.g., Trash) — separated by a divider */}
                {(() => {
                  const pinnedEndIcons = visibleConfig.icons.filter(
                    (i) => i.pinned === "end"
                  );
                  if (pinnedEndIcons.length === 0) return null;
                  return (
                    <>
                      {/* macOS-style dock divider */}
                      <div
                        style={{
                          width: "1px",
                          height: "32px",
                          backgroundColor: "rgba(255, 255, 255, 0.3)",
                          flexShrink: 0,
                          alignSelf: "center",
                          marginLeft: "2px",
                          marginRight: "2px",
                        }}
                      />
                      {pinnedEndIcons.map((iconConfig) => {
                        const windowInstance = windowManager.windows.get(
                          iconConfig.windowId
                        );
                        const windowIsOpen =
                          windowInstance?.state.status === "open" ||
                          windowInstance?.state.status === "minimized";
                        const isActive = iconConfig.isActiveIcon
                          ? iconConfig.isActiveIcon(windowIsOpen)
                          : windowIsOpen;

                        return (
                          <DesktopIconWrapper
                            key={iconConfig.id}
                            config={iconConfig}
                            onClick={() => handleIconClick(iconConfig)}
                            onRegisterRef={registerIconRef}
                            isMobile={false}
                            isDock={true}
                            isOpen={isActive}
                            dockHeight={dockHeight}
                          />
                        );
                      })}
                    </>
                  );
                })()}
              </div>
              {/* Desktop icons (moved out of dock) */}
              {desktopPreferences.desktopIcons.map((di) => {
                // Check if this is a group
                const group = desktopPreferences.iconGroups[di.id];
                if (group) {
                  const groupIconConfigs = group.iconIds
                    .map((id) => visibleConfig.icons.find((i) => i.id === id))
                    .filter((c): c is DesktopIconConfig => !!c);
                  return (
                    <div
                      key={di.id}
                      data-hurricane-icon
                      style={{
                        position: "absolute",
                        left: `${di.x}%`,
                        top: `${di.y}%`,
                        transform: "translate(-50%, -50%)",
                        zIndex: draggingDesktopIconId === di.id ? 9999 : 0,
                      }}
                    >
                      <GroupIcon
                        iconConfigs={groupIconConfigs}
                        groupName={group.name}
                        groupId={di.id}
                        badgeCount={group.iconIds.reduce(
                          (sum, id) => sum + (desktopBadgeCounts.get(id) ?? 0),
                          0
                        )}
                        onClick={() => {
                          // TODO: open group overlay on desktop
                        }}
                      />
                    </div>
                  );
                }

                const iconConfig = visibleConfig.icons.find(
                  (i) => i.id === di.id
                );
                if (!iconConfig) return null;

                const windowInstance = windowManager.windows.get(
                  iconConfig.windowId
                );
                const windowIsOpen =
                  windowInstance?.state.status === "open" ||
                  windowInstance?.state.status === "minimized";
                const isActive = iconConfig.isActiveIcon
                  ? iconConfig.isActiveIcon(windowIsOpen)
                  : windowIsOpen;

                return (
                  <div
                    key={iconConfig.id}
                    data-hurricane-icon
                    style={{
                      position: "absolute",
                      left: `${di.x}%`,
                      top: `${di.y}%`,
                      transform: "translate(-50%, -50%)",
                      zIndex:
                        draggingDesktopIconId === iconConfig.id ? 9999 : 0,
                    }}
                  >
                    <DesktopIconWrapper
                      config={iconConfig}
                      onClick={() => handleIconClick(iconConfig)}
                      onRegisterRef={registerIconRef}
                      isMobile={false}
                      isDock={false}
                      isOpen={isActive}
                      onPositionChange={(id, x, y) =>
                        desktopPreferences.updateDesktopIconPosition(id, x, y)
                      }
                      onContextMenu={(id, x, y) =>
                        setContextMenu({ iconId: id, x, y })
                      }
                      onDragToDock={(id, cursorX) => {
                        // Only allow dragging to dock if under the max limit
                        if (
                          dockVisibleIcons.length < maxDockIcons ||
                          dockVisibleIcons.includes(id)
                        ) {
                          // moveIconToDock updates dockVisibleIcons via the hook
                          // Map visual insertion index to dockOrder index
                          const visualIdx = getDockInsertionIndex(cursorX);
                          let dockOrderIdx: number;
                          if (visualIdx >= dockVisibleIcons.length) {
                            const lastVisibleId =
                              dockVisibleIcons[dockVisibleIcons.length - 1];
                            dockOrderIdx =
                              desktopPreferences.dockIcons.indexOf(
                                lastVisibleId
                              ) + 1;
                          } else {
                            const anchorId = dockVisibleIcons[visualIdx];
                            dockOrderIdx =
                              desktopPreferences.dockIcons.indexOf(anchorId);
                          }
                          desktopPreferences.moveIconToDock(id, dockOrderIdx);
                        } else {
                          // Dock is full — snap icon back above the dock
                          const safeY =
                            ((window.innerHeight - dockHeight - 40) /
                              window.innerHeight) *
                            100;
                          const currentDi =
                            desktopPreferences.desktopIcons.find(
                              (d) => d.id === id
                            );
                          if (currentDi) {
                            desktopPreferences.updateDesktopIconPosition(
                              id,
                              currentDi.x,
                              Math.min(currentDi.y, safeY)
                            );
                          }
                        }
                      }}
                      dockHeight={dockHeight}
                      onDraggingChange={handleDesktopIconDraggingChange}
                    />
                  </div>
                );
              })}
              {/* Takeoff launcher — leftmost icon in the dock */}
            </>
          ) : effectiveLayoutMode === "left" ||
            effectiveLayoutMode === "right" ? (
            <div
              data-hurricane-dock
              style={{
                position: "absolute",
                top: MENU_BAR_HEIGHT + 12,
                [effectiveLayoutMode === "left" ? "left" : "right"]: 0,
                bottom: 40,
                display: "flex",
                flexDirection: "column",
                flexWrap: "wrap",
                alignContent:
                  effectiveLayoutMode === "left" ? "flex-start" : "flex-end",
                gap: `${iconLayout.gap}px`,
                padding: `${iconLayout.padding}px`,
                zIndex: 10,
                transition: "transform 0.5s ease-out",
                transform: showIcons
                  ? "translateX(0)"
                  : effectiveLayoutMode === "left"
                    ? "translateX(-300px)"
                    : "translateX(300px)",
              }}
            >
              {desktopPreferences.allIconIds.map((iconId) => {
                const iconConfig = visibleConfig.icons.find(
                  (i) => i.id === iconId
                );
                if (!iconConfig) return null;

                const windowInstance = windowManager.windows.get(
                  iconConfig.windowId
                );
                const windowIsOpen =
                  windowInstance?.state.status === "open" ||
                  windowInstance?.state.status === "minimized";
                const isActive = iconConfig.isActiveIcon
                  ? iconConfig.isActiveIcon(windowIsOpen)
                  : windowIsOpen;

                return (
                  <DesktopIconWrapper
                    key={iconConfig.id}
                    config={iconConfig}
                    onClick={() => handleIconClick(iconConfig)}
                    onRegisterRef={registerIconRef}
                    isMobile={false}
                    isDock={false}
                    isOpen={isActive}
                  />
                );
              })}
            </div>
          ) : (
            /* Free positioning mode */
            <>
              {desktopPreferences.allIconIds.map((iconId) => {
                const iconConfig = visibleConfig.icons.find(
                  (i) => i.id === iconId
                );
                if (!iconConfig) return null;

                const placement = desktopPreferences.desktopIcons.find(
                  (d) => d.id === iconId
                );
                const x = placement?.x ?? 10;
                const y = placement?.y ?? 10;

                const windowInstance = windowManager.windows.get(
                  iconConfig.windowId
                );
                const windowIsOpen =
                  windowInstance?.state.status === "open" ||
                  windowInstance?.state.status === "minimized";
                const isActive = iconConfig.isActiveIcon
                  ? iconConfig.isActiveIcon(windowIsOpen)
                  : windowIsOpen;

                return (
                  <div
                    key={iconConfig.id}
                    data-hurricane-icon
                    style={{
                      position: "absolute",
                      left: `${x}%`,
                      top: `${y}%`,
                      transform: "translate(-50%, -50%)",
                      zIndex:
                        draggingDesktopIconId === iconConfig.id ? 9999 : 0,
                    }}
                  >
                    <DesktopIconWrapper
                      config={iconConfig}
                      onClick={() => handleIconClick(iconConfig)}
                      onRegisterRef={registerIconRef}
                      isMobile={false}
                      isDock={false}
                      isOpen={isActive}
                      onPositionChange={(id, xp, yp) =>
                        desktopPreferences.updateDesktopIconPosition(id, xp, yp)
                      }
                      onDraggingChange={handleDesktopIconDraggingChange}
                    />
                  </div>
                );
              })}
            </>
          )}

          {/* Windows */}
          {Array.from(windowManager.windows.values()).map((instance) => {
            // Find the icon for this window (for animations)
            const iconConfig = visibleConfig.icons.find(
              (i) => i.windowId === instance.config.id
            );
            const iconRef = iconConfig
              ? iconRefs.current.get(iconConfig.id)
              : undefined;

            const getIconPosition = iconRef?.current
              ? () => iconRef.current!.getPosition()
              : undefined;

            // Get persisted geometry for this window
            const persistedGeometry = windowManager.geometryStates.get(
              instance.config.id
            );

            return (
              <Window
                key={instance.config.id}
                config={instance.config}
                state={instance.state}
                actions={instance.actions}
                isMobile={isMobile}
                keyboardOpen={dockHiddenByKeyboard}
                dockHeight={dockHeight}
                menuBarHeight={shouldAutoHideMenuBar ? 0 : MENU_BAR_HEIGHT}
                getIconPosition={getIconPosition}
                persistedGeometry={persistedGeometry}
                onGeometryChange={(geometry) =>
                  windowManager.updateGeometry(instance.config.id, geometry)
                }
                keepMounted={isWindowMinimized(instance.state)}
                previewRect={(() => {
                  const slot = appSwitcherSlots.get(instance.config.id);
                  return slot
                    ? {
                        x: slot.x,
                        y: slot.y,
                        width: slot.width,
                        height: slot.height,
                      }
                    : undefined;
                })()}
              />
            );
          })}

          {/* Drag-to-close target — trash icon appears when dragging a window (mobile only) */}
          {isMobile && <WindowDragCloseTarget />}

          {/* Dock drag ghost — rendered outside the dock container to sit above its stacking context */}
          {dockDragGhost &&
            (() => {
              const ghostIcon = visibleConfig.icons.find(
                (i) => i.id === dockDragGhost.iconId
              );
              if (!ghostIcon) return null;
              const isString = typeof ghostIcon.icon === "string";
              return (
                <div
                  style={{
                    position: "fixed",
                    left: dockDragGhost.cursorX,
                    top: dockDragGhost.cursorY,
                    transform: "translate(-50%, -50%) scale(1.15)",
                    zIndex: 9999,
                    pointerEvents: "none",
                    opacity: 0.9,
                  }}
                  className="flex flex-col items-center"
                >
                  <div
                    className={`pointer-events-none ${isString ? "text-4xl" : "flex h-12 w-12 items-center justify-center"}`}
                  >
                    {ghostIcon.icon}
                  </div>
                </div>
              );
            })()}

          {/* Widget Layer (desktop only) */}
          {!isMobile && (
            <WidgetLayer
              isOpen={showWidgets}
              onClose={() => setShowWidgets(false)}
            />
          )}

          {/* Spotlight Search — includes all icons so hidden apps are searchable */}
          <Spotlight
            isOpen={showSpotlight}
            onClose={() => setShowSpotlight(false)}
            icons={visibleConfig.icons}
            onIconClick={handleIconClick}
          />

          {/* App Switcher — surface for jumping between minimized (backgrounded) apps */}
          <AppSwitcher
            isOpen={showAppSwitcher}
            onClose={() => setShowAppSwitcher(false)}
            isMobile={isMobile}
            icons={visibleConfig.icons}
            onSlotsChange={setAppSwitcherSlots}
          />

          {/* Takeoff App Launcher — shows all non-pinned icons including hidden ones */}
          <Takeoff
            isOpen={showTakeoff}
            onClose={() => setShowTakeoff(false)}
            icons={visibleConfig.icons.filter((i) => !i.pinned)}
            onIconClick={handleIconClick}
            onOpenSpotlight={() => setShowSpotlight(true)}
            isIconInDock={(iconId) => dockVisibleIcons.includes(iconId)}
            isIconOnDesktop={desktopPreferences.isIconOnDesktop}
            isDockFull={dockVisibleIcons.length >= maxDockIcons}
            onMoveToDock={(iconId) => {
              if (dockVisibleIcons.length < maxDockIcons) {
                desktopPreferences.moveIconToDock(iconId);
              }
            }}
            onMoveToDesktop={(iconId) => {
              const xPercent = 15 + Math.random() * 30;
              const yPercent = 15 + Math.random() * 30;
              desktopPreferences.moveIconToDesktop(iconId, xPercent, yPercent);
            }}
            onHideIcon={desktopPreferences.hideIcon}
          />

          {/* Icon Context Menu */}
          {contextMenu && !isMobile && (
            <IconContextMenu
              position={{ x: contextMenu.x, y: contextMenu.y }}
              isInDock={dockVisibleIcons.includes(contextMenu.iconId)}
              isDockMode={desktopPreferences.layoutMode === "dock"}
              dockFull={
                dockVisibleIcons.length >= maxDockIcons &&
                !dockVisibleIcons.includes(contextMenu.iconId)
              }
              canHide={
                !visibleConfig.icons.find((i) => i.id === contextMenu.iconId)
                  ?.pinned
              }
              isRunning={(() => {
                const iconConfig = visibleConfig.icons.find(
                  (i) => i.id === contextMenu.iconId
                );
                if (!iconConfig) return false;
                const windowInstance = windowManager.windows.get(
                  iconConfig.windowId
                );
                const windowIsOpen =
                  windowInstance?.state.status === "open" ||
                  windowInstance?.state.status === "minimized";
                return iconConfig.isActiveIcon
                  ? iconConfig.isActiveIcon(windowIsOpen)
                  : windowIsOpen;
              })()}
              onQuit={() => {
                const iconConfig = visibleConfig.icons.find(
                  (i) => i.id === contextMenu.iconId
                );
                if (iconConfig) windowManager.closeWindow(iconConfig.windowId);
              }}
              onMoveToDock={() => {
                if (
                  dockVisibleIcons.length < maxDockIcons ||
                  dockVisibleIcons.includes(contextMenu.iconId)
                ) {
                  desktopPreferences.moveIconToDock(contextMenu.iconId);
                }
              }}
              onMoveToDesktop={() => {
                // Place at cursor position converted to percentages
                const xPercent = (contextMenu.x / window.innerWidth) * 100;
                const yPercent = (contextMenu.y / window.innerHeight) * 100;
                desktopPreferences.moveIconToDesktop(
                  contextMenu.iconId,
                  xPercent,
                  yPercent
                );
              }}
              onHide={() => desktopPreferences.hideIcon(contextMenu.iconId)}
              onClose={() => setContextMenu(null)}
            />
          )}

          {/* Notification Toasts */}
          <NotificationToasts isDark={isDark} />

          {/* Custom children */}
          {children}
        </div>
      </DesktopProvider>
    </NotificationProvider>
  );
}

/* ==========================================================================
   HELPER COMPONENTS
   ========================================================================== */

interface DesktopIconWrapperProps {
  config: DesktopIconConfig;
  onClick: () => void;
  onRegisterRef: (
    iconId: string,
    ref: RefObject<DesktopIconRef | null>
  ) => void;
  isMobile: boolean;
  isDock?: boolean;
  isOpen?: boolean;
  isMobileGrid?: boolean;
  isJiggling?: boolean;
  onDockReorder?: (fromIndex: number, cursorX: number) => void;
  dockIndex?: number;
  onPositionChange?: (
    iconId: string,
    xPercent: number,
    yPercent: number
  ) => void;
  onContextMenu?: (iconId: string, x: number, y: number) => void;
  onDragOffDock?: (iconId: string, xPercent: number, yPercent: number) => void;
  onDragToDock?: (iconId: string, cursorX: number) => void;
  dockHeight?: number;
  onDockDragMove?: (iconId: string, cursorX: number, cursorY: number) => void;
  onDockDragEnd?: (iconId: string) => void;
  onDraggingChange?: (iconId: string, isDragging: boolean) => void;
}

/**
 * Wrapper component that registers the icon ref on mount.
 */
function DesktopIconWrapper({
  config,
  onClick,
  onRegisterRef,
  isMobile,
  isDock = false,
  isOpen = false,
  isMobileGrid = false,
  isJiggling = false,
  onDockReorder,
  dockIndex,
  onPositionChange,
  onContextMenu,
  onDragOffDock,
  onDragToDock,
  dockHeight,
  onDockDragMove,
  onDockDragEnd,
  onDraggingChange,
}: DesktopIconWrapperProps) {
  const ref = useRef<DesktopIconRef>(null);

  useEffect(() => {
    onRegisterRef(config.id, ref);
  }, [config.id, onRegisterRef]);

  return (
    <DesktopIcon
      ref={ref}
      config={config}
      onClick={onClick}
      isDock={isDock}
      isOpen={isOpen}
      isMobile={isMobile}
      isMobileGrid={isMobileGrid}
      isJiggling={isJiggling}
      onDockReorder={onDockReorder}
      dockIndex={dockIndex}
      onPositionChange={onPositionChange}
      onContextMenu={onContextMenu}
      onDragOffDock={onDragOffDock}
      onDragToDock={onDragToDock}
      dockHeight={dockHeight}
      onDockDragMove={onDockDragMove}
      onDockDragEnd={onDockDragEnd}
      onDraggingChange={onDraggingChange}
      badgeCount={0}
    />
  );
}

/* ==========================================================================
   MOBILE ICON GRID COMPONENT
   ========================================================================== */

interface MobileIconGridProps {
  icons: DesktopIconConfig[];
  mobileGridPositions: Record<string, import("@repo/types").MobileGridPosition>;
  mobileDockPositions: Record<string, import("@repo/types").MobileDockPosition>;
  windowManager: ReturnType<typeof useWindowManager>;
  onIconClick: (iconConfig: DesktopIconConfig) => void;
  registerIconRef: (
    iconId: string,
    ref: RefObject<DesktopIconRef | null>
  ) => void;
  showIcons: boolean;
  onOpenSpotlight: () => void;
  /** Open the app switcher overlay (null = hide the button) */
  onOpenAppSwitcher?: () => void;
  /** Count of minimized (backgrounded) windows — drives the button's badge */
  appSwitcherCount?: number;
  onPlaceInGrid: (
    iconId: string,
    page: number,
    row: number,
    col: number
  ) => void;
  onPlaceInDock: (iconId: string, col: number) => void;
  onRemoveFromGrid: (iconId: string) => void;
  onAddToHomeScreen: (iconId: string) => void;
  isSpotlightOpen: boolean;
  /** Icon groups (folders) */
  iconGroups: Record<string, import("@repo/types").IconGroup>;
  /** Callbacks for group operations */
  onCreateGroup: (
    iconIdA: string,
    iconIdB: string,
    position:
      | { type: "grid"; page: number; row: number; col: number }
      | { type: "dock"; col: number }
  ) => string;
  onDissolveGroup: (groupId: string) => void;
  onAddToGroup: (groupId: string, iconId: string) => void;
  onRemoveFromGroup: (groupId: string, iconId: string) => void;
  onRenameGroup: (groupId: string, name: string) => void;
  onReorderGroup: (groupId: string, newOrder: string[]) => void;
}

/**
 * iOS-style icon grid with horizontal page swiping.
 * Supports sparse placement: icons can be at any (page, row, col) with gaps.
 */
function MobileIconGrid({
  icons,
  mobileGridPositions,
  mobileDockPositions,
  windowManager,
  onIconClick,
  registerIconRef,
  showIcons,
  onOpenSpotlight,
  onOpenAppSwitcher,
  appSwitcherCount = 0,
  onPlaceInGrid,
  onPlaceInDock,
  onRemoveFromGrid,
  onAddToHomeScreen,
  isSpotlightOpen,
  iconGroups,
  onCreateGroup,
  onDissolveGroup,
  onAddToGroup,
  onRemoveFromGroup,
  onRenameGroup,
  onReorderGroup,
}: MobileIconGridProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageGridRef = useRef<HTMLDivElement>(null);
  const dockBarRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchMoveRef = useRef({ x: 0, y: 0, scrollLeft: 0 });
  const [onSearchPage, setOnSearchPage] = useState(false);

  // Group overlay state
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  const openGroupAnchorRef = useRef<DOMRect | null>(null);

  // Mobile context menu state
  const [mobileContextMenu, setMobileContextMenu] = useState<{
    iconId: string;
    source: "grid" | "dock";
    x: number;
    y: number;
  } | null>(null);

  // Grid dimensions
  const COLS = 4;
  const ROWS = 5;

  // Build icon lookup map
  const iconMap = useMemo(() => {
    const map = new Map<string, DesktopIconConfig>();
    for (const icon of icons) map.set(icon.id, icon);
    return map;
  }, [icons]);

  // Badge counts for group aggregation
  const badgeCounts = useAppBadgeCounts();
  const getGroupBadgeCount = useCallback(
    (groupId: string) => {
      const group = iconGroups[groupId];
      if (!group) return 0;
      return group.iconIds.reduce(
        (sum, id) => sum + (badgeCounts.get(id) ?? 0),
        0
      );
    },
    [iconGroups, badgeCounts]
  );

  // Build sparse page grids from positions (supports both icons and groups)
  type GridCell =
    | { kind: "icon"; config: DesktopIconConfig }
    | { kind: "group"; groupId: string }
    | null;

  const { pageGrids, totalPages } = useMemo(() => {
    const maxPage = Math.max(
      0,
      ...Object.values(mobileGridPositions).map((p) => p.page)
    );
    const pageCount = Math.max(1, maxPage + 1);

    const grids: GridCell[][][] = [];
    for (let p = 0; p < pageCount; p++) {
      const grid: GridCell[][] = [];
      for (let r = 0; r < ROWS; r++) {
        grid.push(Array(COLS).fill(null));
      }
      grids.push(grid);
    }

    for (const [id, pos] of Object.entries(mobileGridPositions)) {
      if (pos.page >= pageCount || pos.row >= ROWS || pos.col >= COLS) continue;
      const group = iconGroups[id];
      if (group) {
        grids[pos.page][pos.row][pos.col] = { kind: "group", groupId: id };
      } else {
        const config = iconMap.get(id);
        if (config) {
          grids[pos.page][pos.row][pos.col] = { kind: "icon", config };
        }
      }
    }

    return { pageGrids: grids, totalPages: pageCount };
  }, [mobileGridPositions, iconMap, iconGroups]);

  // Build dock slots: 4 fixed slots, some may be null (supports groups)
  const dockSlots = useMemo(() => {
    const slots: GridCell[] = [null, null, null, null];
    for (const [id, pos] of Object.entries(mobileDockPositions)) {
      if (pos.col < 0 || pos.col >= COLS) continue;
      const group = iconGroups[id];
      if (group) {
        slots[pos.col] = { kind: "group", groupId: id };
      } else {
        const config = iconMap.get(id);
        if (config) {
          slots[pos.col] = { kind: "icon", config };
        }
      }
    }
    return slots;
  }, [mobileDockPositions, iconMap, iconGroups]);

  const hasDockIcons = dockSlots.some((s) => s !== null);

  // Clamp page when pages change (moved after drag hook to access displayTotalPages)

  // FLIP animation: smoothly animate icons to new grid positions after displacement
  const iconRectsRef = useRef<Map<string, DOMRect>>(new Map());
  const flipContainerRef = useRef<HTMLDivElement>(null);

  // Snapshot current icon positions (called before a drop commits)
  const snapshotIconPositions = useCallback(() => {
    const container = flipContainerRef.current;
    if (!container) return;
    const rects = new Map<string, DOMRect>();
    const elements = container.querySelectorAll<HTMLElement>("[data-flip-id]");
    elements.forEach((el) => {
      const id = el.getAttribute("data-flip-id");
      if (id) {
        rects.set(id, el.getBoundingClientRect());
      }
    });
    iconRectsRef.current = rects;
  }, []);

  // After React re-renders with new positions, animate icons from old to new position
  useLayoutEffect(() => {
    const prevRects = iconRectsRef.current;
    if (prevRects.size === 0) return;

    const container = flipContainerRef.current;
    if (!container) return;

    const elements = container.querySelectorAll<HTMLElement>("[data-flip-id]");
    elements.forEach((el) => {
      const id = el.getAttribute("data-flip-id");
      if (!id) return;
      const oldRect = prevRects.get(id);
      if (!oldRect) return;

      const newRect = el.getBoundingClientRect();
      const dx = oldRect.left - newRect.left;
      const dy = oldRect.top - newRect.top;

      // Only animate if the icon actually moved
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;

      // FLIP: apply inverse transform so icon appears at old position
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      el.style.transition = "none";

      // Play: in the next frame, remove the transform with a smooth transition
      requestAnimationFrame(() => {
        el.style.transition = "transform 0.25s cubic-bezier(0.2, 0, 0, 1)";
        el.style.transform = "";

        const cleanup = () => {
          el.style.transition = "";
          el.removeEventListener("transitionend", cleanup);
        };
        el.addEventListener("transitionend", cleanup);
      });
    });

    // Clear the snapshot so we don't re-animate on unrelated re-renders
    iconRectsRef.current = new Map();
  }, [mobileGridPositions, mobileDockPositions]);

  // Wrap the placement callbacks to snapshot before committing
  const handlePlaceInGrid = useCallback(
    (iconId: string, page: number, row: number, col: number) => {
      snapshotIconPositions();
      onPlaceInGrid(iconId, page, row, col);
    },
    [snapshotIconPositions, onPlaceInGrid]
  );

  const handlePlaceInDock = useCallback(
    (iconId: string, col: number) => {
      snapshotIconPositions();
      onPlaceInDock(iconId, col);
    },
    [snapshotIconPositions, onPlaceInDock]
  );

  // Drag hook — pass totalPages + 1 so edge-scrolling can create a new page during drag
  const drag = useMobileIconDrag({
    mobileGridPositions,
    mobileDockPositions,
    currentPage,
    setCurrentPage,
    totalPages: totalPages + 1,
    cols: COLS,
    rows: ROWS,
    pageContainerRef: pageGridRef,
    dockContainerRef: dockBarRef,
    onPlaceInGrid: handlePlaceInGrid,
    onPlaceInDock: handlePlaceInDock,
    onContextMenu: useCallback(
      (iconId: string, source: "grid" | "dock", x: number, y: number) => {
        // Groups don't get a context menu — they use the overlay + jiggle mode X button
        if (iconId.startsWith("group_")) return;
        setMobileContextMenu({ iconId, source, x, y });
      },
      []
    ),
    onDismissContextMenu: useCallback(() => {
      setMobileContextMenu(null);
    }, []),
    iconGroups,
    onCreateGroup: useCallback(
      (
        iconIdA: string,
        iconIdB: string,
        position:
          | { type: "grid"; page: number; row: number; col: number }
          | { type: "dock"; col: number }
      ) => {
        snapshotIconPositions();
        return onCreateGroup(iconIdA, iconIdB, position);
      },
      [snapshotIconPositions, onCreateGroup]
    ),
    onAddToGroup: useCallback(
      (groupId: string, iconId: string) => {
        snapshotIconPositions();
        onAddToGroup(groupId, iconId);
      },
      [snapshotIconPositions, onAddToGroup]
    ),
  });

  // During jiggle mode, append an extra empty page so users can drag icons to a new page
  const displayPageGrids = useMemo(() => {
    if (!drag.isJiggling) return pageGrids;
    const emptyPage: GridCell[][] = [];
    for (let r = 0; r < ROWS; r++) {
      emptyPage.push(Array(COLS).fill(null));
    }
    return [...pageGrids, emptyPage];
  }, [pageGrids, drag.isJiggling]);

  const showAppLibrary = !drag.isJiggling;
  const appLibraryPageIndex = displayPageGrids.length;
  const displayTotalPages = displayPageGrids.length + (showAppLibrary ? 1 : 0);

  // Clamp page when pages change
  useEffect(() => {
    if (currentPage >= displayTotalPages)
      setCurrentPage(Math.max(0, displayTotalPages - 1));
  }, [currentPage, displayTotalPages]);

  // Search page: open Spotlight after a brief delay when entering search page
  const spotlightOpenedOnSearchRef = useRef(false);
  useEffect(() => {
    if (!onSearchPage) {
      spotlightOpenedOnSearchRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      onOpenSpotlight();
      spotlightOpenedOnSearchRef.current = true;
    }, 350);
    return () => clearTimeout(timer);
  }, [onSearchPage, onOpenSpotlight]);

  // When Spotlight closes while on search page, navigate back to page 0
  useEffect(() => {
    if (
      onSearchPage &&
      spotlightOpenedOnSearchRef.current &&
      !isSpotlightOpen
    ) {
      setOnSearchPage(false);
      setCurrentPage(0);
    }
  }, [onSearchPage, isSpotlightOpen]);

  // Combined touch handlers: swipe for page navigation + drag forwarding
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (drag.isJiggling && drag.draggedIconId) return;
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      touchMoveRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        scrollLeft: containerRef.current?.scrollLeft ?? 0,
      };
    },
    [drag.isJiggling, drag.draggedIconId]
  );

  const handleCombinedTouchMove = useCallback(
    (e: React.TouchEvent) => {
      drag.handleTouchMove(e);
      if (!drag.isJiggling || !drag.draggedIconId) {
        touchMoveRef.current.x = e.touches[0].clientX;
        touchMoveRef.current.y = e.touches[0].clientY;
      }
    },
    [drag]
  );

  const handleCombinedTouchEnd = useCallback(() => {
    drag.handleTouchEnd();

    if (!drag.isJiggling || !drag.draggedIconId) {
      // On App Library page, check if this was primarily a vertical scroll
      if (showAppLibrary && currentPage === appLibraryPageIndex) {
        const absY = Math.abs(touchStartRef.current.y - touchMoveRef.current.y);
        const absX = Math.abs(touchStartRef.current.x - touchMoveRef.current.x);
        if (absY > absX) return; // vertical scroll, don't swipe pages
      }

      const deltaX = touchStartRef.current.x - touchMoveRef.current.x;
      const threshold = 50;
      if (Math.abs(deltaX) > threshold) {
        if (onSearchPage && deltaX > 0) {
          // Swipe left on search page → go back to page 0
          setOnSearchPage(false);
          setCurrentPage(0);
        } else if (deltaX > 0 && currentPage < displayTotalPages - 1) {
          setCurrentPage((p) => p + 1);
        } else if (deltaX < 0 && currentPage > 0) {
          setCurrentPage((p) => p - 1);
        } else if (deltaX < 0 && currentPage === 0 && !onSearchPage) {
          // Swipe right past page 0 → go to search page
          setOnSearchPage(true);
        }
      }
    }
  }, [
    drag,
    currentPage,
    displayTotalPages,
    showAppLibrary,
    appLibraryPageIndex,
    onSearchPage,
  ]);

  // Helper to check if icon is active
  const getIsActive = (iconConfig: DesktopIconConfig) => {
    const windowInstance = windowManager.windows.get(iconConfig.windowId);
    const windowIsOpen =
      windowInstance?.state.status === "open" ||
      windowInstance?.state.status === "minimized";
    return iconConfig.isActiveIcon
      ? iconConfig.isActiveIcon(windowIsOpen)
      : windowIsOpen;
  };

  // Find dragged icon/group config for ghost
  const draggedIconConfig = drag.draggedIconId
    ? iconMap.get(drag.draggedIconId)
    : null;
  const draggedGroupConfig = drag.draggedIconId
    ? iconGroups[drag.draggedIconId]
    : null;

  return (
    <div
      ref={flipContainerRef}
      className="absolute inset-0 flex flex-col"
      style={{
        top: 28 + 20,
        bottom: hasDockIcons ? 8 : 20,
        left: 0,
        right: 0,
        // Scope text-selection + long-press callout suppression to the mobile
        // homescreen container only (icons, pages, dock). Windows are siblings
        // of this element, so the global opt-in policy still applies inside
        // app windows.
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
      }}
      onTouchMove={drag.handleTouchMove}
      onTouchEnd={drag.handleTouchEnd}
      onClick={
        drag.isJiggling && !drag.draggedIconId
          ? drag.handleBackgroundTap
          : undefined
      }
    >
      {/* Done button during jiggle mode */}
      {drag.isJiggling && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            drag.exitJiggleMode();
          }}
          className="absolute right-4 top-1 z-10 rounded-full px-3 py-1 text-sm font-semibold text-white"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.25)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
          }}
        >
          Done
        </button>
      )}

      {/* Pages container */}
      <div
        ref={containerRef}
        className="relative flex-1 overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleCombinedTouchMove}
        onTouchEnd={handleCombinedTouchEnd}
      >
        <div
          className="absolute inset-0 flex transition-transform duration-300 ease-out"
          style={{
            // +1 page offset because search page is prepended at position 0
            transform: `translateX(-${(onSearchPage ? 0 : currentPage + 1) * 100}%)`,
            opacity: showIcons ? 1 : 0,
            transition: showIcons
              ? "transform 0.3s ease-out, opacity 0.5s ease-out"
              : "opacity 0.5s ease-out",
          }}
        >
          {/* Search page — empty page that Spotlight appears over */}
          <div key="search-page" className="h-full min-w-full" />

          {displayPageGrids.map((pageGrid, pageIndex) => (
            <div
              key={pageIndex}
              className="flex h-full min-w-full items-start justify-center px-4 pt-3"
            >
              <div
                ref={pageIndex === currentPage ? pageGridRef : undefined}
                data-hurricane-dock
                className="grid w-full gap-y-2"
                style={{
                  gridTemplateColumns: `repeat(${COLS}, 1fr)`,
                  gridTemplateRows: `repeat(${ROWS}, auto)`,
                  justifyItems: "center",
                }}
              >
                {pageGrid.flat().map((cell, cellIndex) => {
                  const row = Math.floor(cellIndex / COLS);
                  const col = cellIndex % COLS;
                  const isHoverTarget =
                    drag.hoverCell !== null &&
                    drag.hoverCell.page === pageIndex &&
                    drag.hoverCell.row === row &&
                    drag.hoverCell.col === col;

                  if (!cell) {
                    // Empty cell — acts as a drop target
                    return (
                      <div
                        key={`empty-${pageIndex}-${row}-${col}`}
                        className="w-[78px]"
                        style={{
                          height: 72,
                          borderRadius: 18,
                          backgroundColor:
                            isHoverTarget && drag.draggedIconId
                              ? "rgba(255, 255, 255, 0.15)"
                              : "transparent",
                          transition: "background-color 0.15s ease",
                        }}
                      />
                    );
                  }

                  if (cell.kind === "group") {
                    const group = iconGroups[cell.groupId];
                    if (!group) return null;
                    const groupIconConfigs = group.iconIds
                      .map((id) => iconMap.get(id))
                      .filter((c): c is DesktopIconConfig => !!c);
                    const isDragged = cell.groupId === drag.draggedIconId;
                    const isGroupTarget =
                      cell.groupId === drag.groupHoverTarget;

                    return (
                      <div
                        key={cell.groupId}
                        data-flip-id={cell.groupId}
                        style={{
                          opacity: isDragged ? 0.3 : 1,
                          transform: isGroupTarget ? "scale(1.15)" : "scale(1)",
                          transition:
                            "opacity 0.1s ease, transform 0.2s cubic-bezier(0.2, 0, 0, 1.2)",
                          zIndex: isDragged ? 0 : isGroupTarget ? 2 : 1,
                          filter: isGroupTarget
                            ? "brightness(1.2) drop-shadow(0 0 8px rgba(255,255,255,0.4))"
                            : "none",
                        }}
                        onTouchStart={(e) => {
                          drag.handleIconTouchStart(cell.groupId, "grid", e);
                        }}
                      >
                        <GroupIcon
                          iconConfigs={groupIconConfigs}
                          groupName={group.name}
                          groupId={cell.groupId}
                          isMobileGrid={true}
                          isJiggling={drag.isJiggling}
                          badgeCount={getGroupBadgeCount(cell.groupId)}
                          onClick={() => {
                            if (!drag.isJiggling) {
                              // Capture anchor position for overlay animation
                              const el = document.querySelector(
                                `[data-flip-id="${cell.groupId}"]`
                              );
                              if (el) {
                                openGroupAnchorRef.current =
                                  el.getBoundingClientRect();
                              }
                              setOpenGroupId(cell.groupId);
                            }
                          }}
                          onDelete={() => onDissolveGroup(cell.groupId)}
                        />
                      </div>
                    );
                  }

                  const iconConfig = cell.config;
                  const isDragged = iconConfig.id === drag.draggedIconId;
                  const isGroupTarget = iconConfig.id === drag.groupHoverTarget;

                  return (
                    <div
                      key={iconConfig.id}
                      data-flip-id={iconConfig.id}
                      style={{
                        opacity: isDragged ? 0.3 : 1,
                        transform: isGroupTarget ? "scale(1.15)" : "scale(1)",
                        transition:
                          "opacity 0.1s ease, transform 0.2s cubic-bezier(0.2, 0, 0, 1.2)",
                        zIndex: isDragged ? 0 : isGroupTarget ? 2 : 1,
                        filter: isGroupTarget
                          ? "brightness(1.2) drop-shadow(0 0 8px rgba(255,255,255,0.4))"
                          : "none",
                      }}
                      onTouchStart={(e) => {
                        drag.handleIconTouchStart(iconConfig.id, "grid", e);
                      }}
                    >
                      <DesktopIconWrapper
                        config={iconConfig}
                        onClick={
                          drag.isJiggling
                            ? () => {}
                            : () => onIconClick(iconConfig)
                        }
                        onRegisterRef={registerIconRef}
                        isMobile={true}
                        isMobileGrid={true}
                        isJiggling={drag.isJiggling}
                        isOpen={getIsActive(iconConfig)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* App Library page — alphabetical grid of all apps */}
          {showAppLibrary && (
            <div
              key="app-library-page"
              className="relative h-full min-w-full"
              style={{ overflow: "hidden" }}
            >
              <MobileAppLibrary
                icons={icons}
                onIconClick={onIconClick}
                onLongPress={(iconId: string, x: number, y: number) => {
                  setMobileContextMenu({ iconId, source: "grid", x, y });
                }}
                mobileGridPositions={mobileGridPositions}
                mobileDockPositions={mobileDockPositions}
                windows={windowManager.windows}
              />
            </div>
          )}
        </div>
      </div>

      {/* Mobile context menu */}
      {mobileContextMenu && (
        <MobileContextMenu
          position={{ x: mobileContextMenu.x, y: mobileContextMenu.y }}
          iconLabel={iconMap.get(mobileContextMenu.iconId)?.label ?? ""}
          isInAppLibrary={currentPage === appLibraryPageIndex}
          onRemoveFromHomeScreen={() =>
            onRemoveFromGrid(mobileContextMenu.iconId)
          }
          onAddToHomeScreen={() => onAddToHomeScreen(mobileContextMenu.iconId)}
          onClose={() => setMobileContextMenu(null)}
        />
      )}

      {/* Search button and page indicator dots */}
      {!drag.isJiggling && !onSearchPage && (
        <div className="flex flex-col items-center gap-3 py-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onOpenSpotlight}
              className="flex items-center gap-2 rounded-full px-4 py-2 transition-all duration-200 active:scale-95"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <svg
                className="h-4 w-4 text-white/80"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <span className="text-sm text-white/80">Search</span>
            </button>

            {/* App Switcher pill — only when there are minimized windows */}
            {onOpenAppSwitcher && appSwitcherCount > 0 && (
              <button
                onClick={onOpenAppSwitcher}
                aria-label={`App switcher (${appSwitcherCount} background)`}
                className="flex items-center gap-1.5 rounded-full px-3 py-2 transition-all duration-200 active:scale-95"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                }}
              >
                <svg
                  className="h-4 w-4 text-white/80"
                  viewBox="0 0 14 14"
                  aria-hidden="true"
                >
                  <g fill="currentColor">
                    <rect x="1" y="1" width="4" height="4" rx="1" />
                    <rect x="9" y="1" width="4" height="4" rx="1" />
                    <rect x="1" y="9" width="4" height="4" rx="1" />
                    <rect x="9" y="9" width="4" height="4" rx="1" />
                  </g>
                </svg>
                <span className="text-sm font-medium text-white/85">
                  {appSwitcherCount}
                </span>
              </button>
            )}
          </div>

          {displayTotalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              {Array.from({ length: displayTotalPages }).map((_, index) => {
                const isActive = index === currentPage;
                const isAppLibrary =
                  showAppLibrary && index === appLibraryPageIndex;
                if (isAppLibrary) {
                  return (
                    <button
                      key="app-library-dot"
                      onClick={() => setCurrentPage(index)}
                      aria-label="App Library"
                      className="transition-all duration-200"
                      style={{ opacity: isActive ? 1 : 0.4 }}
                    >
                      <svg
                        width={isActive ? 12 : 10}
                        height={isActive ? 12 : 10}
                        viewBox="0 0 24 24"
                        fill="white"
                      >
                        <rect x="2" y="2" width="9" height="9" rx="2" />
                        <rect x="13" y="2" width="9" height="9" rx="2" />
                        <rect x="2" y="13" width="9" height="9" rx="2" />
                        <rect x="13" y="13" width="9" height="9" rx="2" />
                      </svg>
                    </button>
                  );
                }
                return (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(index)}
                    className={`h-2 w-2 rounded-full transition-all duration-200 ${isActive ? "h-2.5 w-2.5 bg-white" : "bg-white/40"} `}
                    aria-label={`Go to page ${index + 1}`}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Page dots during jiggle (compact) */}
      {drag.isJiggling && displayTotalPages > 1 && (
        <div className="flex items-center justify-center gap-2 py-2">
          {Array.from({ length: displayTotalPages }).map((_, index) => (
            <div
              key={index}
              className={`h-1.5 w-1.5 rounded-full ${index === currentPage ? "bg-white" : "bg-white/40"}`}
            />
          ))}
        </div>
      )}

      {/* iPhone-style mobile dock — always 4 slots */}
      <div
        ref={dockBarRef}
        className="mx-4 mb-2 grid"
        style={{
          gridTemplateColumns: `repeat(${COLS}, 1fr)`,
          justifyItems: "center",
          padding: "8px 0",
          backgroundColor:
            drag.hoverDockCol !== null
              ? "rgba(255, 255, 255, 0.25)"
              : "rgba(255, 255, 255, 0.15)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderRadius: 24,
          border:
            drag.hoverDockCol !== null
              ? "1px solid rgba(255, 255, 255, 0.4)"
              : "1px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          minHeight: 82,
          opacity: showIcons && !onSearchPage ? 1 : 0,
          transform:
            showIcons && !onSearchPage ? "translateY(0)" : "translateY(40px)",
          transition:
            "opacity 0.4s ease-out, transform 0.4s ease-out, background-color 0.2s, border-color 0.2s",
        }}
      >
        {dockSlots.map((cell, col) => {
          if (!cell) {
            // Empty dock slot
            const isHoverTarget =
              drag.hoverDockCol === col && drag.draggedIconId;
            return (
              <div
                key={`dock-empty-${col}`}
                className="w-[78px]"
                style={{
                  height: 72,
                  borderRadius: 18,
                  backgroundColor: isHoverTarget
                    ? "rgba(255, 255, 255, 0.15)"
                    : "transparent",
                  transition: "background-color 0.15s ease",
                }}
              />
            );
          }

          if (cell.kind === "group") {
            const group = iconGroups[cell.groupId];
            if (!group) return null;
            const groupIconConfigs = group.iconIds
              .map((id) => iconMap.get(id))
              .filter((c): c is DesktopIconConfig => !!c);
            const isDragged =
              cell.groupId === drag.draggedIconId && drag.dragSource === "dock";
            return (
              <div
                key={cell.groupId}
                data-flip-id={cell.groupId}
                style={{
                  opacity: isDragged ? 0.3 : 1,
                  transition: "opacity 0.15s ease",
                }}
                onTouchStart={(e) => {
                  drag.handleIconTouchStart(cell.groupId, "dock", e);
                }}
              >
                <GroupIcon
                  iconConfigs={groupIconConfigs}
                  groupName={group.name}
                  groupId={cell.groupId}
                  isMobileGrid={true}
                  isDock={false}
                  isJiggling={drag.isJiggling}
                  badgeCount={getGroupBadgeCount(cell.groupId)}
                  onClick={() => {
                    if (!drag.isJiggling) {
                      const el = document.querySelector(
                        `[data-flip-id="${cell.groupId}"]`
                      );
                      if (el) {
                        openGroupAnchorRef.current = el.getBoundingClientRect();
                      }
                      setOpenGroupId(cell.groupId);
                    }
                  }}
                  onDelete={() => onDissolveGroup(cell.groupId)}
                />
              </div>
            );
          }

          const iconConfig = cell.config;
          const isDragged =
            iconConfig.id === drag.draggedIconId && drag.dragSource === "dock";
          return (
            <div
              key={iconConfig.id}
              data-flip-id={iconConfig.id}
              style={{
                opacity: isDragged ? 0.3 : 1,
                transition: "opacity 0.15s ease",
              }}
              onTouchStart={(e) => {
                drag.handleIconTouchStart(iconConfig.id, "dock", e);
              }}
            >
              <DesktopIconWrapper
                config={iconConfig}
                onClick={
                  drag.isJiggling ? () => {} : () => onIconClick(iconConfig)
                }
                onRegisterRef={registerIconRef}
                isMobile={true}
                isDock={true}
                isJiggling={drag.isJiggling}
                isOpen={getIsActive(iconConfig)}
              />
            </div>
          );
        })}
      </div>

      {/* Drag ghost — positioned via ref for 60fps, no React re-renders */}
      {drag.draggedIconId &&
        drag.ghostInitial &&
        (draggedIconConfig || draggedGroupConfig) && (
          <div
            ref={drag.ghostRef}
            style={{
              position: "fixed",
              left: 0,
              top: 0,
              transform: `translate(${drag.ghostInitial.x}px, ${drag.ghostInitial.y}px) translate(-50%, -50%) scale(1.1)`,
              willChange: "transform",
              zIndex: 9999,
              opacity: 0.9,
              pointerEvents: "none",
              filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.4))",
            }}
          >
            {draggedGroupConfig ? (
              <GroupIcon
                iconConfigs={draggedGroupConfig.iconIds
                  .map((id) => iconMap.get(id))
                  .filter((c): c is DesktopIconConfig => !!c)}
                groupName={draggedGroupConfig.name}
                groupId={draggedGroupConfig.id}
                isMobileGrid={true}
                badgeCount={getGroupBadgeCount(draggedGroupConfig.id)}
              />
            ) : draggedIconConfig ? (
              <DesktopIconWrapper
                config={draggedIconConfig}
                onClick={() => {}}
                onRegisterRef={() => {}}
                isMobile={true}
                isMobileGrid={true}
                isOpen={false}
              />
            ) : null}
          </div>
        )}

      {/* Group overlay */}
      {openGroupId && iconGroups[openGroupId] && (
        <GroupOverlay
          group={iconGroups[openGroupId]}
          iconConfigs={iconGroups[openGroupId].iconIds
            .map((id) => iconMap.get(id))
            .filter((c): c is DesktopIconConfig => !!c)}
          anchorRect={openGroupAnchorRef.current}
          onClose={() => {
            setOpenGroupId(null);
            openGroupAnchorRef.current = null;
          }}
          onRename={(name) => onRenameGroup(openGroupId, name)}
          onIconClick={(config) => {
            onIconClick(config);
            setOpenGroupId(null);
            openGroupAnchorRef.current = null;
          }}
          onRemoveIcon={(iconId) => {
            onRemoveFromGroup(openGroupId, iconId);
            // If auto-dissolved (group will have <=1 icon), close overlay
            const group = iconGroups[openGroupId];
            if (group && group.iconIds.length <= 2) {
              setOpenGroupId(null);
              openGroupAnchorRef.current = null;
            }
          }}
          onReorderIcons={(newOrder) => onReorderGroup(openGroupId, newOrder)}
        />
      )}
    </div>
  );
}
